import { default as Skeleton } from 'components/ui/molecules/Skeleton';
import { Tooltip } from 'shared/legacy/components/Tooltip';
import { jobBoardIconSizes, useStyles } from './styles';

import {
  Database_01,
  Debanensite,
  Indeed,
  Jobbird,
  Linkedin,
  Monsterboard,
  Nvb,
  Resumeio,
  RobinIconMonoFulfilled,
  Studentjob,
  Techniekwerkt,
  Uitzendbureau,
  Werknl,
} from '@recruit-robin/robin-theme/web-icons';

import { useMemo, useState } from 'react';
import {
  CampaignJobboard,
  CampaignJobboardLabel,
} from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.types';
import { useGetAllAtsConnectionsQuery } from 'shared/legacy/hooks/useGetAllAtsConnectionsQuery';
import type { JobBoardIconProps, JobBoardIconSize } from './types';

const JobBoardIcon = ({
  onClick,
  disabledReason,
  variant = CampaignJobboard.Linkedin,
  inactive = false,
  disabled = false,
  isLoading,
  size = 'medium',
  isActivating = false,
  showJobBoardNameTooltip = false,
  backgroundColor,
  color,
}: JobBoardIconProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const classes = useStyles({
    disabled,
    inactive,
    size,
    isActivating,
    pointer: Boolean(onClick),
    jobboardVariant: variant,
    backgroundColor,
    isLoading,
    color,
  });

  const {
    data: { activeATS },
  } = useGetAllAtsConnectionsQuery();

  const toolTipTitle: React.ReactNode = useMemo(() => {
    if (disabled && disabledReason) {
      return disabledReason;
    }
    let title = CampaignJobboardLabel[variant];
    if (variant === CampaignJobboard.Database && activeATS?.ats_name) {
      title += ' ' + activeATS?.ats_name;
    }
    return title;
  }, [disabled, disabledReason, variant]);

  return (
    <Tooltip title={toolTipTitle} disabled={!showJobBoardNameTooltip} enablePortal>
      <div css={classes.wrapper} onClick={onClick} className={'job-board-icon'}>
        {isLoading ? (
          <Skeleton variant="circle" size={jobBoardIconSizes[size]} />
        ) : (
          <div css={classes.content} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            {jobBoardIcon[variant]?.(size, isHovering && !inactive)}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default JobBoardIcon;

const jobBoardIcon: Record<
  CampaignJobboard,
  (size: JobBoardIconSize, enableSecondaryColor?: boolean) => React.ReactNode
> = {
  [CampaignJobboard.Linkedin]: (size) => <Linkedin size={size === 'big' ? 14 : 14} />,
  [CampaignJobboard.Werknl]: (size) => <Werknl size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.Indeed]: (size) => (
    <span style={{ marginLeft: -2 }}>
      <Indeed size={size === 'big' ? 16 : 12} />
    </span>
  ),
  [CampaignJobboard.Monsterboard]: (size) => <Monsterboard size={size === 'big' ? 13 : 9} />,
  [CampaignJobboard.Resumeio]: (size) => <Resumeio size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.Database]: (size) => <Database_01 size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.Debanensite]: (size) => <Debanensite size={size === 'big' ? 14 : 12} />,
  [CampaignJobboard.Jobbird]: (size) => <Jobbird size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.NationaleVacaturebank]: (size) => <Nvb size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.Techniekwerkt]: (size) => <Techniekwerkt size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.Uitzendbureau]: (size) => <Uitzendbureau size={size === 'big' ? 16 : 12} />,
  [CampaignJobboard.StudentJob]: (size) => <Studentjob size={size === 'big' ? 29 : 18} />,
  [CampaignJobboard.RobinResume]: (size) => <RobinIconMonoFulfilled size={size === 'big' ? 16 : 12} />,

  /// TODO WERKZOEKEN: Remove this after release new jobboard
  // [CampaignJobboard.Werkzoeken]: (size) => <Werkzoeken size={size === 'big' ? 16 : 12} />,
};
